// features/chat-feature.js — CHAT ISLAND
// ═══════════════════════════════════════════════════════════
// Self-contained chat feature. V1 scroll engine + V1 styling.
// Scroll engine from hooks/use-chat-thread.js.
// Message rendering via MessageRow component.
// Two separate setMessages calls — critical for pin timing.
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { generateId } from '../lib/render-helpers';
import MessageRow from '../components/message-row';
import HomeScreen from '../components/home-screen';
import { useVoice } from '../hooks/use-voice';
import { useChatThread } from '../hooks/use-chat-thread';
import SlashPicker from './slash-picker';
import ThreadArtifactPanel from '../components/thread-artifact-panel';
import HighlightToolbar from '../components/highlight-toolbar';
import { useChatLifecycle } from '../hooks/use-chat-lifecycle';
import ChatInputBar from '../components/chat-input-bar';
import ChatThreadHeader from '../components/chat-thread-header';

export default function ChatFeature({
  userId, supabase, userRecord,
  currentSessionId, setCurrentSessionId,
  threadHistory, setThreadHistory,
  voiceConfig,
  pendingThreadId, onClearPendingThread,
  pendingNewThread, onClearPendingNewThread,
  isIncognito, onLeaveIncognito,
  onRenameThread,
}) {
  // ── State ────────────────────────────────────────────────
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionLoading, setSessionLoading] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !!localStorage.getItem('shift_last_session');
  });
  const [deepSearchActive, setDeepSearchActive] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showArtifactPanel, setShowArtifactPanel] = useState(false);
  const [artifactIconHover, setArtifactIconHover] = useState(false);
  const [highlightRefreshKey, setHighlightRefreshKey] = useState(0);
  const [artifactPanelWidth, setArtifactPanelWidth] = useState(280);
  const [showSlashPicker, setShowSlashPicker] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [pinnedReference, setPinnedReference] = useState(null);
  const [pinnedRefExpanded, setPinnedRefExpanded] = useState(false);
  const [playbookItems, setPlaybookItems] = useState([]);

  useEffect(() => {
    if (!currentSessionId || messages.length === 0) return;
    localStorage.setItem('shift_last_session', currentSessionId);
  }, [currentSessionId, messages.length]);

  // ── Refs not owned by useChatThread ──
  const loadedEmptyRef           = useRef(false);
  const artifactContainerRef     = useRef(null);
  const voiceSourceRef           = useRef(false);
  const voiceResponseCallbackRef = useRef(null);

  const { beforeSend, buildRequest, onResponseDone } = useChatLifecycle({
    userId, supabase, currentSessionId, userRecord, isIncognito,
    pendingFile, setPendingFile, deepSearchActive, setDeepSearchActive, setThreadHistory,
  });

  // ── useChatThread — owns scroll, SSE, send mechanics ──
  const chat = useChatThread({
    messages, setMessages,
    typingAnimation: true,
    beforeSend,
    buildRequest,
    onResponseDone,
  });

  const { scrollContainerRef, lastUserMsgRef, inputRef, spacerRef, loading, showScrollBtn, send: chatSend, abort, scrollToBottom } = chat;

  function submitMessage(directText) {
    const text = (directText !== undefined ? directText : input).trim();
    if (!text && !pendingFile) return;
    chatSend(text);
    setInput('');
    if (inputRef.current) inputRef.current.innerText = '';
  }
  const handleSend = submitMessage;

  // ── Voice (defined AFTER useChatThread) ──
  const { voiceListening, voiceEnabled, toggleVoice } = useVoice({
    session: voiceConfig?.session || null,
    userRecord,
    currentSessionId,
    threadHistory: voiceConfig?.threadHistory || [],
    voiceEnabled: voiceConfig?.voiceEnabled ?? true,
    inputRef,
    setInput,
    send: handleSend,
    voiceSourceRef,
    voiceResponseCallbackRef,
  });

  // ── Helper functions ────────────────────────────────────
  async function handleRenameSubmit() {
    if (!titleDraft.trim() || titleDraft === sessionTitle) { setEditingTitle(false); return; }
    await onRenameThread?.(currentSessionId, titleDraft.trim());
    setEditingTitle(false);
  }

  function startResize(e) {
    e.preventDefault();
    const startX = e.clientX, startWidth = artifactPanelWidth;
    const onMove = e => setArtifactPanelWidth(Math.min(600, Math.max(220, startWidth + (startX - e.clientX))));
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  function startNewThread() {
    localStorage.removeItem('shift_last_session');
    setMessages([]); setCurrentSessionId(generateId()); setInput('');
    if (inputRef.current) inputRef.current.innerText = '';
    loadedEmptyRef.current = false; setSessionLoading(false);
    setShowArtifactPanel(false); setDeepSearchActive(false); setPendingFile(null);
  }

  async function loadThread(sessionId) {
    setShowArtifactPanel(false); setDeepSearchActive(false); setPendingFile(null);
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const res = await fetch(`/api/user/messages?sessionId=${encodeURIComponent(sessionId)}`, { headers: { 'Authorization': `Bearer ${authSession?.access_token || ''}` } });
      const { messages: data } = res.ok ? await res.json() : { messages: [] };
      if (data && data.length > 0) {
        loadedEmptyRef.current = false; setMessages(data); setCurrentSessionId(sessionId);
        localStorage.setItem('shift_last_session', sessionId);
      } else {
        loadedEmptyRef.current = false; setMessages([]); setCurrentSessionId(generateId());
        localStorage.removeItem('shift_last_session');
      }
    } catch (e) { console.error('loadThread:', e); }
  }

  // ── Effects ─────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    supabase.from('playbook_items').select('id, title, type:item_type, content').eq('owner_id', userId).order('created_at', { ascending: false }).limit(50).then(({ data }) => { if (data) setPlaybookItems(data); });
    if (pendingThreadId || pendingNewThread) return;
    if (localStorage.getItem('shift_active_space')) return;
    const savedSessionId = localStorage.getItem('shift_last_session');
    if (savedSessionId) { setSessionLoading(true); loadThread(savedSessionId).finally(() => setSessionLoading(false)); }
  }, [userId]);

  useEffect(() => { if (pendingThreadId) { loadThread(pendingThreadId); onClearPendingThread?.(); } }, [pendingThreadId]);
  useEffect(() => { if (pendingNewThread) { startNewThread(); onClearPendingNewThread?.(); } }, [pendingNewThread]);

  function handleInputChange(val) {
    const trimmed = val.trim();
    if (trimmed.startsWith('/') && trimmed.length > 1) { setSlashQuery(trimmed.slice(1).toLowerCase()); setShowSlashPicker(true); }
    else { setShowSlashPicker(false); setSlashQuery(''); }
  }

  function compressAndSetFile({ base64, name, type }) {
    const isImage = type.startsWith('image/'), isPDF = type === 'application/pdf';
    if (isImage) {
      const img = new window.Image();
      img.onload = () => {
        const MAX = 1500; let w = img.width, h = img.height;
        if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } }
        const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        setPendingFile({ base64: canvas.toDataURL('image/jpeg', 0.7).split(',')[1], name, type: 'image/jpeg', isImage: true, isPDF: false });
        if (inputRef.current) inputRef.current.focus();
      };
      img.src = base64.includes(',') ? base64 : `data:${type};base64,${base64}`;
    } else {
      setPendingFile({ base64: base64.includes(',') ? base64.split(',')[1] : base64, name, type, isImage, isPDF });
      if (inputRef.current) inputRef.current.focus();
    }
  }

  // ── Render ───────────────────────────────────────────────
  const lastUserIndex = messages.reduce((acc, m, i) => m.role === 'user' ? i : acc, -1);
  const sessionTitle = threadHistory?.find(t => t.id === currentSessionId)?.title || null;

  return (
    <div ref={artifactContainerRef} style={{
      position: 'relative', height: '100%',
      display: 'flex', flexDirection: 'row',
      overflow: 'hidden',
    }}>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

      <ChatThreadHeader
        sessionTitle={sessionTitle}
        isIncognito={isIncognito}
        editingTitle={editingTitle}
        titleDraft={titleDraft}
        setEditingTitle={setEditingTitle}
        setTitleDraft={setTitleDraft}
        onRenameSubmit={handleRenameSubmit}
        showArtifactPanel={showArtifactPanel}
        onToggleArtifactPanel={() => {
          if (!showArtifactPanel) {
            const containerWidth = artifactContainerRef.current?.offsetWidth || 900;
            setArtifactPanelWidth(Math.round(containerWidth * 0.33));
          }
          setShowArtifactPanel(p => !p);
        }}
        artifactIconHover={artifactIconHover}
        setArtifactIconHover={setArtifactIconHover}
        currentSessionId={currentSessionId}
        messages={messages}
        userId={userId}
        supabase={supabase}
        onLeaveIncognito={onLeaveIncognito}
      />

      {/* Empty state — closed incognito session */}
      {messages.length === 0 && !sessionLoading && isIncognito && loadedEmptyRef.current && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#2a2a2a', fontSize: 13, fontFamily: 'Georgia, serif', fontStyle: 'italic', textAlign: 'center', padding: '60px 24px' }}>
            This was a private session. Nothing was kept.
          </div>
        </div>
      )}

      {/* Empty state — normal (includes active incognito) */}
      {messages.length === 0 && !sessionLoading && !loadedEmptyRef.current && (
        <HomeScreen
          userRecord={userRecord}
          onSend={(text) => handleSend(text)}
          input={input}
          setInput={setInput}
          voiceEnabled={voiceEnabled}
          voiceListening={voiceListening}
          toggleVoice={toggleVoice}
          onVoiceToggle={voiceConfig?.onVoiceToggle}
          pendingFile={pendingFile}
          onClearFile={() => { setPendingFile(null); setInput(''); if (inputRef.current) inputRef.current.innerText = ''; }}
          deepSearchActive={deepSearchActive}
          onDeepSearch={() => setDeepSearchActive(v => !v)}
          onFileUpload={({ base64, name, type }) => compressAndSetFile({ base64, name, type })}
        />
      )}

      {/* Chat mode */}
      {messages.length > 0 && (
        <>
          {/* SCROLL DIV — flex:1, no padding */}
          <div
            ref={scrollContainerRef}
            className="messages-scroll shift-scrollbar"
            style={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              maxWidth: 'var(--content-max-width)',
              margin: '0 auto',
              width: '100%',
            }}
          >
            <div style={{ padding: '60px 60px 0 60px', boxSizing: 'border-box' }}>
              {messages.map((m, i) => (
                <MessageRow
                  key={m.id || i}
                  m={m}
                  i={i}
                  isLastUser={i === lastUserIndex}
                  lastUserMsgRef={lastUserMsgRef}
                />
              ))}
              <div ref={spacerRef} style={{ height: 0, flexShrink: 0 }} aria-hidden="true" />
            </div>
          </div>

          {/* Scroll-to-bottom button */}
          {showScrollBtn && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: '-40px', left: '50%', transform: 'translateX(-50%)', zIndex: 40 }}>
                <button
                  onClick={scrollToBottom}
                  style={{
                    background: '#111118', border: '1px solid #2a2a2a',
                    borderRadius: '50%', width: 32, height: 32,
                    cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#444'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3v10M3.5 8.5L8 13l4.5-4.5" stroke="#888" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* INPUT BAR — flex sibling, outside scroll */}
          <div className="shift-input-bar" style={{ flexShrink: 0, width: '100%', background: '#07070a', position: 'relative', zIndex: 5 }}>
            <div style={{ maxWidth: 'var(--content-max-width)', margin: '0 auto', padding: '0 10px 17px', boxSizing: 'border-box' }}>
              <SlashPicker
                inputRef={inputRef}
                pinnedRefExpanded={pinnedRefExpanded}
                pinnedReference={pinnedReference}
                playbookItems={playbookItems}
                setInput={setInput}
                setPinnedRefExpanded={setPinnedRefExpanded}
                setPinnedReference={setPinnedReference}
                setShowSlashPicker={setShowSlashPicker}
                showSlashPicker={showSlashPicker}
                slashQuery={slashQuery}
                session={null}
                isMobile={false}
              />
              <ChatInputBar
                inputRef={inputRef}
                value={input}
                setValue={setInput}
                onSend={() => handleSend()}
                loading={loading}
                onAbort={abort}
                pendingFile={pendingFile}
                setPendingFile={setPendingFile}
                onFileSelect={({ base64, name, type }) => compressAndSetFile({ base64, name, type })}
                deepSearchActive={deepSearchActive}
                setDeepSearchActive={setDeepSearchActive}
                voiceListening={voiceListening}
                voiceEnabled={voiceEnabled}
                onVoiceToggle={() => {
                  const next = !voiceEnabled;
                  voiceConfig?.onVoiceToggle?.(next);
                  if (next) toggleVoice();
                  else if (voiceListening) toggleVoice();
                }}
                placeholder="What's on your mind"
                onInputChange={handleInputChange}
              />
            </div>
          </div>
        </>
      )}
    </div>

    {/* Artifact panel — right side */}
    {showArtifactPanel && (
      <div
        onMouseDown={startResize}
        style={{ width: 4, background: 'var(--border-4)', cursor: 'col-resize', flexShrink: 0, transition: 'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = '#c8a96e33'}
        onMouseLeave={e => e.currentTarget.style.background = 'var(--border-4)'}
      />
    )}
    {showArtifactPanel && (
      <ThreadArtifactPanel
        userId={userId}
        supabase={supabase}
        currentSessionId={currentSessionId}
        width={artifactPanelWidth}
        highlightRefreshKey={highlightRefreshKey}
        onArtifactSelect={() => {
          const containerWidth = artifactContainerRef.current?.offsetWidth || 900;
          setArtifactPanelWidth(Math.round(containerWidth * 0.50));
        }}
      />
    )}

    <HighlightToolbar
      userId={userId}
      supabase={supabase}
      sessionId={currentSessionId}
      onSaved={() => setHighlightRefreshKey(k => k + 1)}
      onReply={(text) => {
        const quoted = `"${text}"\n`;
        setInput(quoted);
        if (inputRef.current) {
          inputRef.current.innerText = quoted;
          inputRef.current.focus();
        }
      }}
    />
    </div>
  );
}
